import { ContactMail, Language, PhoneAndroid, Save, SmartDisplay } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import apiService from "../../../service/Api";
import { RootState } from "../../../store/Index";
import { tokens } from "../../../theme";
import AppLinksPanel, { AppLink } from "./components/AppsLinksPanel";
import ContactPanel, { ContactDetail } from "./components/ContactPanel";
import SocialsPanel, { SocialLink } from "./components/SocialsPanel";
import ColorPicker from "./components/ColorPicker";
import { usePermissions } from "../../../hooks/usePermissions";
import { PERMISSIONS } from "../../../constants/permissions";

const SOURCE_OPTIONS = [
  "PixelPilot",
  "MMS",
  "ClientMemorialPage",
  "LivestreamingWebsite",
  "MuzukuruWebsite",
  "ChemaFundraiser",
];

const TABS = [
  { label: "Social Links", icon: <Language fontSize="small" /> },
  //   { label: "Navigation", icon: <ViewColumn fontSize="small" /> },
  { label: "Contact", icon: <ContactMail fontSize="small" /> },
  { label: "App Downloads", icon: <PhoneAndroid fontSize="small" /> },
  { label: "Hero Video", icon: <SmartDisplay fontSize="small" /> },
];
const HERO_VIDEO_TAB = TABS.length - 1;

interface HeroVideoConfigRecord {
  id: number;
  source: string;
  youtube_url: string;
  header: string;
  description: string;
}

/** Mirrors muzukuru-website/lib/heroVideo.ts's toYouTubeEmbedUrl exactly, so
 * the preview here matches what the public site will actually render. */
function toYouTubeEmbedUrl(url: string): string | null {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace(/^www\./, "");

    if (host === "youtu.be") {
      const id = parsed.pathname.slice(1);
      return id ? `https://www.youtube.com/embed/${id}` : null;
    }

    if (host === "youtube.com" || host === "m.youtube.com") {
      if (parsed.pathname === "/watch") {
        const id = parsed.searchParams.get("v");
        return id ? `https://www.youtube.com/embed/${id}` : null;
      }
      if (parsed.pathname.startsWith("/embed/")) {
        return `https://www.youtube.com${parsed.pathname}`;
      }
      if (parsed.pathname.startsWith("/shorts/")) {
        const id = parsed.pathname.split("/")[2];
        return id ? `https://www.youtube.com/embed/${id}` : null;
      }
    }

    return null;
  } catch {
    return null;
  }
}

type PaginatedResponse<T> = {
  results?: T[];
};

function getResults<T>(data: T[] | PaginatedResponse<T>): T[] {
  return Array.isArray(data) ? data : data.results ?? [];
}

const FooterLinksManager: React.FC = () => {
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission(PERMISSIONS.CONFIGURATION_CHANGE);
  const [activeTab, setActiveTab] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  const theme = useTheme();
  const themeState = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, themeState);

  const primaryColor = colors.primary[400];
  const secondaryColor = colors.primary[500];
  const bgColor = colors.primary[900];
  const [socials, setSocials] = useState<SocialLink[]>([]);
  const [contactDetails, setContactDetails] = useState<ContactDetail[]>([]);
  const [appLinks, setAppLinks] = useState<AppLink[]>([]);
  const [source, setSource] = useState<string>();
  const [configId, setConfigId] = useState<number | null>(null);
  const [backgroundColor, setBackgroundColor] = useState("");
  const [textColor, setTextColor] = useState("");

  const [heroVideoConfigId, setHeroVideoConfigId] = useState<number | null>(null);
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [heroHeader, setHeroHeader] = useState("");
  const [heroDescription, setHeroDescription] = useState("");

  const [allConfigs, setAllConfigs] = useState<
    { id: number; source: string }[]
  >([]);

  useEffect(() => {
    const fetchAllConfigs = async () => {
      try {
        const res = await apiService.get<{ id: number; source: string }[]>(
          `/api/configurations/footer-config/`,
        );
        const configs = getResults(res.data).map((c) => ({ id: c.id, source: c.source }));
        setAllConfigs(configs);
        if (configs.length > 0) setSource(configs[0].source);
      } catch {
        console.error("Failed to fetch config list");
      }
    };
    fetchAllConfigs();
  }, []);

  useEffect(() => {
    if (!source) return;
    const fetchConfig = async () => {
      try {
        const res = await apiService.get<
          {
            id: number;
            source: string;
            socials: SocialLink[];
            contact_details: ContactDetail[];
            app_links: AppLink[];
            background_color?: string;
            text_color?: string;
          }[]
        >(`/api/configurations/footer-config/`, { source });
        const config = getResults(res.data).find((c) => c.source === source);
        if (config) {
          setConfigId(config.id);
          setSocials(config.socials.map((s) => ({ ...s, id: String(s.id) })));
          setContactDetails(
            config.contact_details.map((c) => ({ ...c, id: String(c.id) })),
          );
          setAppLinks(
            config.app_links.map((l) => ({ ...l, id: String(l.id) })),
          );
          setBackgroundColor(config.background_color ?? "");
          setTextColor(config.text_color ?? "");
        } else {
          setSocials([]);
          setContactDetails([]);
          setAppLinks([]);
          setConfigId(null);
        }
      } catch {
        toast.error("Failed to load footer config.");
      }
    };
    fetchConfig();
  }, [source]);

  useEffect(() => {
    if (!source) return;
    const fetchHeroVideoConfig = async () => {
      try {
        const res = await apiService.get<HeroVideoConfigRecord[]>(
          `/api/configurations/hero-video-config/`,
          { source },
        );
        const config = getResults(res.data).find((c) => c.source === source);
        if (config) {
          setHeroVideoConfigId(config.id);
          setYoutubeUrl(config.youtube_url ?? "");
          setHeroHeader(config.header ?? "");
          setHeroDescription(config.description ?? "");
        } else {
          setHeroVideoConfigId(null);
          setYoutubeUrl("");
          setHeroHeader("");
          setHeroDescription("");
        }
      } catch {
        toast.error("Failed to load hero video config.");
      }
    };
    fetchHeroVideoConfig();
  }, [source]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const payload = {
        source,
        background_color: backgroundColor || "#000000",
        text_color: textColor || "#ffffff",
        socials: socials.map(({ platform, url, icon }, i) => ({
          platform,
          url,
          icon,
          order: i,
        })),
        contact_details: contactDetails.map(({ type, value }, i) => ({
          type,
          value,
          order: i,
        })),
        app_links: appLinks.map(({ platform, url, label }, i) => ({
          platform,
          url,
          label,
          order: i,
        })),
      };

      if (configId) {
        await apiService.patch(
          `/api/configurations/footer-config/${configId}/`,
          payload,
        );
      } else {
        const res = await apiService.post<{ id: number }>(
          `/api/configurations/footer-config/`,
          payload,
        );
        setConfigId(res.data.id);
      }

      toast.success("Footer configuration saved successfully!");
    } catch {
      toast.error("Failed to save footer configuration.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveHeroVideo = async () => {
    setIsSaving(true);
    try {
      const payload = {
        source,
        youtube_url: youtubeUrl,
        header: heroHeader,
        description: heroDescription,
      };

      if (heroVideoConfigId) {
        await apiService.patch(
          `/api/configurations/hero-video-config/${heroVideoConfigId}/`,
          payload,
        );
      } else {
        const res = await apiService.post<{ id: number }>(
          `/api/configurations/hero-video-config/`,
          payload,
        );
        setHeroVideoConfigId(res.data.id);
      }

      toast.success("Hero video configuration saved successfully!");
    } catch {
      toast.error("Failed to save hero video configuration.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveActiveTab = () =>
    activeTab === HERO_VIDEO_TAB ? handleSaveHeroVideo() : handleSave();

  const heroEmbedUrl = toYouTubeEmbedUrl(youtubeUrl);

  return (
    <Box sx={{ pb: 4 }}>
      <Box
        sx={{
          bgcolor: primaryColor,
          borderRadius: "12px 12px 0 0",
          p: { xs: 3, md: 4 },
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                color: colors.primary[900],
                fontSize: { xs: "1.4rem", md: "1.8rem" },
              }}
            >
              Site Content Manager
            </Typography>
            <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.8)" }}>
              Manage footer links, contacts, app downloads, and the homepage hero video for each site
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <ColorPicker label="Footer Background Color" value={backgroundColor} onChange={setBackgroundColor} />

          {/* <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="caption" color="white" fontWeight={600}>
              Text
            </Typography>
            <Box
              component="input"
              type="color"
              value={textColor}
              onChange={(e) => setTextColor(e.target.value)}
              sx={{
                width: 36,
                height: 36,
                border: "none",
                borderRadius: 50,
                cursor: "pointer",
                padding: 0,
                bgcolor: "transparent",
              }}
            />
          </Box> */}
        </Box>

        {canEdit && (
          <Button
            variant="contained"
            startIcon={
              isSaving ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <Save />
              )
            }
            onClick={handleSaveActiveTab}
            disabled={isSaving}
            sx={{
              bgcolor: colors.primary[900],
              color: primaryColor,
              fontWeight: 700,
              textTransform: "none",
              borderRadius: 2,
              px: 3,
              boxShadow: 0,
              "&:hover": { bgcolor: "rgba(255,255,255,0.88)" },
            }}
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 2,
          py: 1.5,
          flexWrap: "wrap",
          bgcolor: colors.primary[800],
          border: `1px solid ${primaryColor}22`,
          borderTop: "none",
        }}
      >
        <Typography
          variant="caption"
          color={colors.primary[400]}
          fontWeight={700}
          sx={{ mr: 1 }}
        >
          SITES:
        </Typography>

        {SOURCE_OPTIONS.map((s) => {
          const exists = allConfigs.some((c) => c.source === s);
          const isActive = source === s;
          return (
            <Chip
              key={s}
              label={s}
              size="small"
              onClick={() => {
                setSource(s);
                if (!exists) {
                  setConfigId(null);
                  setSocials([]);
                  setContactDetails([]);
                  setAppLinks([]);
                  setHeroVideoConfigId(null);
                  setYoutubeUrl("");
                  setHeroHeader("");
                  setHeroDescription("");
                }
              }}
              variant={isActive ? "filled" : "outlined"}
              sx={{
                fontWeight: 600,
                borderColor: exists ? primaryColor : `${primaryColor}55`,
                borderStyle: exists ? "solid" : "dashed",
                color: isActive
                  ? colors.primary[900]
                  : exists
                    ? primaryColor
                    : colors.primary[300],
                bgcolor: isActive ? primaryColor : "transparent",
                "&:hover": { bgcolor: `${primaryColor}22` },
              }}
            />
          );
        })}
      </Box>

      {/* Tabs */}
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${primaryColor}22`,
          borderTop: "none",
          borderRadius: "0 0 12px 12px",
          overflow: "hidden",
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: `1px solid ${primaryColor}22`,
            px: 2,
            "& .MuiTabs-indicator": { backgroundColor: primaryColor },
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.9rem",
              color: colors.primary[300],
              "&.Mui-selected": { color: primaryColor },
            },
          }}
        >
          {TABS.map((tab, i) => (
            <Tab
              key={tab.label}
              label={tab.label}
              icon={tab.icon}
              iconPosition="start"
              value={i}
            />
          ))}
        </Tabs>

        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              {activeTab === 0 && (
                <SocialsPanel
                  socials={socials}
                  setSocials={setSocials}
                  primaryColor={primaryColor}
                  secondaryColor={secondaryColor}
                  bgColor={bgColor}
                />
              )}
              {/* {activeTab === 1 && (
                <NavColumnsPanel
                  navColumns={navColumns}
                  setNavColumns={setNavColumns}
                  primaryColor={primaryColor}
                  bgColor={bgColor}
                />
              )} */}
              {activeTab === 1 && (
                <ContactPanel
                  contactDetails={contactDetails}
                  setContactDetails={setContactDetails}
                  primaryColor={primaryColor}
                  secondaryColor={secondaryColor}
                  bgColor={bgColor}
                />
              )}
              {activeTab === 2 && (
                <AppLinksPanel
                  appLinks={appLinks}
                  setAppLinks={setAppLinks}
                  primaryColor={primaryColor}
                  secondaryColor={secondaryColor}
                  bgColor={bgColor}
                />
              )}
              {activeTab === HERO_VIDEO_TAB && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", lg: "row" },
                    gap: 4,
                    alignItems: "flex-start",
                  }}
                >
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, maxWidth: 560, width: "100%" }}>
                    <TextField
                      label="YouTube URL"
                      placeholder="https://www.youtube.com/watch?v=..."
                      value={youtubeUrl}
                      onChange={(e) => setYoutubeUrl(e.target.value)}
                      fullWidth
                      disabled={!canEdit}
                      error={!!youtubeUrl && !heroEmbedUrl}
                      helperText={
                        youtubeUrl && !heroEmbedUrl
                          ? "Doesn't look like a valid YouTube URL."
                          : "Full YouTube video or embed URL."
                      }
                    />
                    <TextField
                      label="Header"
                      placeholder="Heading shown below the video"
                      value={heroHeader}
                      onChange={(e) => setHeroHeader(e.target.value)}
                      fullWidth
                      disabled={!canEdit}
                    />
                    <TextField
                      label="Description"
                      placeholder="Description shown below the header"
                      value={heroDescription}
                      onChange={(e) => setHeroDescription(e.target.value)}
                      fullWidth
                      multiline
                      minRows={3}
                      disabled={!canEdit}
                    />
                  </Box>

                  <Box sx={{ width: "100%", maxWidth: 420 }}>
                    <Typography
                      variant="caption"
                      color={colors.primary[300]}
                      fontWeight={700}
                      sx={{ display: "block", mb: 1 }}
                    >
                      LIVE PREVIEW
                    </Typography>
                    {heroEmbedUrl ? (
                      <Box
                        sx={{
                          position: "relative",
                          width: "100%",
                          pb: "56.25%",
                          borderRadius: 2,
                          overflow: "hidden",
                          boxShadow: 2,
                        }}
                      >
                        <Box
                          component="iframe"
                          src={heroEmbedUrl}
                          title={heroHeader || "Preview"}
                          sx={{
                            position: "absolute",
                            inset: 0,
                            width: "100%",
                            height: "100%",
                            border: 0,
                          }}
                          allow="accelerometer;autoplay;clipboard-write;encrypted-media;gyroscope;picture-in-picture"
                          allowFullScreen
                        />
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          width: "100%",
                          pb: "56.25%",
                          position: "relative",
                          borderRadius: 2,
                          bgcolor: colors.primary[800],
                          border: `1px dashed ${primaryColor}55`,
                        }}
                      >
                        <Typography
                          variant="body2"
                          color={colors.primary[300]}
                          sx={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                            px: 2,
                          }}
                        >
                          {youtubeUrl
                            ? "Invalid URL — preview unavailable"
                            : "Enter a YouTube URL to preview"}
                        </Typography>
                      </Box>
                    )}
                    {(heroHeader || heroDescription) && (
                      <Box sx={{ mt: 2, textAlign: "center" }}>
                        {heroHeader && (
                          <Typography sx={{ fontWeight: 700, fontSize: 16, color: colors.primary[400] }}>
                            {heroHeader}
                          </Typography>
                        )}
                        {heroDescription && (
                          <Typography variant="body2" color={colors.primary[700]} sx={{ mt: 0.5 }}>
                            {heroDescription}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
            </motion.div>
          </AnimatePresence>
        </Box>
      </Paper>
    </Box>
  );
};

export default FooterLinksManager;
